import { useMemo, useState } from 'react';
import {
  Stack, Title, Card, Group, Table, Text, Skeleton, ScrollArea,
  TextInput, Select, MultiSelect, Checkbox, Badge,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useQuery } from '@tanstack/react-query';
import { IconArrowsSort, IconSortAscending, IconSortDescending } from '@tabler/icons-react';
import { api } from '../api/client.ts';

/**
 * Travel report page. Mirrors the legacy /travel route — top-bar server
 * filters (from/to/types) reload via /api/travel-reports, plus per-column
 * in-table filters that operate on the loaded result set. The two location
 * dropdowns are populated from the distinct values currently in the result,
 * which is the "dynamically queried from current resultset" behaviour.
 */
interface ReportRow {
  id: number;
  firstName?: string | null;
  lastName?: string | null;
  countryName?: string | null;
  departureFromName?: string | null;
  departureDate?: string | null;
  arrivalToName?: string | null;
  arrivalDate?: string | null;
  transport?: string | null;
  transportReference?: string | null;
  pickupRequired?: boolean | null;
}

const ALL_TYPES: { value: string; label: string }[] = [
  { value: 'plane', label: 'Plane' },
  { value: 'train', label: 'Train' },
  { value: 'road', label: 'Road' },
  { value: 'stay', label: 'Accomodation' },
];
const DEFAULT_TYPES = ['plane', 'train', 'road'];

type SortDir = 'asc' | 'desc' | null;
type SortField = keyof ReportRow;

export function TravelsPage() {
  // Server-side filters: changing any of these reloads the dataset.
  const [from, setFrom] = useState<Date>(() => new Date());
  const [to, setTo] = useState<Date>(() => new Date(Date.now() + 7 * 86_400_000));
  const [types, setTypes] = useState<string[]>(DEFAULT_TYPES);

  const params = useMemo(
    () => ({ from: ymd(from), to: ymd(to), types: types.join(',') }),
    [from, to, types],
  );

  const query = useQuery({
    queryKey: ['travel-report', params],
    queryFn: async (): Promise<ReportRow[]> => {
      const res = await api.get('/api/travel-reports', { params });
      if (res.status !== 200) throw new Error(`status ${res.status}`);
      return res.data as ReportRow[];
    },
  });

  const rows = query.data ?? [];

  // Client-side filters (per column).
  const [fFirst, setFFirst] = useState('');
  const [fLast, setFLast] = useState('');
  const [fCountry, setFCountry] = useState('');
  const [fDepLoc, setFDepLoc] = useState<string | null>(null);
  const [fArrLoc, setFArrLoc] = useState<string | null>(null);
  const [fReference, setFReference] = useState('');
  // Tri-state pickup: null = both, true = pickup only, false = no-pickup only.
  const [fPickup, setFPickup] = useState<boolean | null>(null);

  // Distinct location values pulled FROM the loaded result set — these drive
  // the two location dropdowns dynamically as the data changes.
  const departureLocations = useMemo(() => distinct(rows.map(r => r.departureFromName)), [rows]);
  const arrivalLocations = useMemo(() => distinct(rows.map(r => r.arrivalToName)), [rows]);

  // Sort state.
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  function toggleSort(field: SortField) {
    if (sortField !== field) {
      setSortField(field); setSortDir('asc');
      return;
    }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortField(null); setSortDir(null);
  }

  const filtered = useMemo(() => {
    const t = (s: string | null | undefined, q: string) =>
      q === '' ? true : (s ?? '').toLowerCase().includes(q.toLowerCase());
    const out = rows.filter(r =>
      t(r.firstName, fFirst) &&
      t(r.lastName, fLast) &&
      t(r.countryName, fCountry) &&
      (fDepLoc == null || r.departureFromName === fDepLoc) &&
      (fArrLoc == null || r.arrivalToName === fArrLoc) &&
      t(r.transportReference, fReference) &&
      (fPickup == null || Boolean(r.pickupRequired) === fPickup),
    );
    if (sortField && sortDir) {
      const dir = sortDir === 'asc' ? 1 : -1;
      out.sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    }
    return out;
  }, [rows, fFirst, fLast, fCountry, fDepLoc, fArrLoc, fReference, fPickup, sortField, sortDir]);

  const colCount = 10;

  return (
    <Stack gap="md">
      <Title order={2}>Travels</Title>

      <Card withBorder padding="md" radius="md">
        <Group align="flex-end" gap="md" wrap="wrap">
          <DatePickerInput
            label="From"
            value={from}
            onChange={d => d && setFrom(d as Date)}
            valueFormat="DD/MM/YYYY"
            w={160}
          />
          <DatePickerInput
            label="To"
            value={to}
            onChange={d => d && setTo(d as Date)}
            valueFormat="DD/MM/YYYY"
            w={160}
          />
          <MultiSelect
            label="Types"
            data={ALL_TYPES}
            value={types}
            onChange={setTypes}
            placeholder="All types"
            clearable
            miw={260}
          />
          <Text c="dimmed" size="sm" ml="auto">
            {filtered.length} / {rows.length} rows
          </Text>
        </Group>
      </Card>

      <Card withBorder padding={0} radius="md">
        {query.isPending ? (
          <Stack gap="xs" p="md">{[1, 2, 3, 4, 5].map(i => <Skeleton key={i} h={28} />)}</Stack>
        ) : query.isError ? (
          <Text c="red" p="md">
            Travel report failed — {(query.error as Error)?.message ?? 'unknown error'}
          </Text>
        ) : (
          <ScrollArea h={620} type="auto">
            <Table striped highlightOnHover stickyHeader fz="sm">
              <Table.Thead>
                <Table.Tr>
                  <Th label="First Name" field="firstName" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Last Name" field="lastName" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Country" field="countryName" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Departure From" field="departureFromName" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Departure Date/Time" field="departureDate" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Arrival To" field="arrivalToName" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Arrival Date/Time" field="arrivalDate" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Type" field="transport" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Number" field="transportReference" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                  <Th label="Pickup" field="pickupRequired" sortField={sortField} sortDir={sortDir} onSort={toggleSort} />
                </Table.Tr>
                <Table.Tr>
                  <Table.Th><TextInput size="xs" value={fFirst} onChange={e => setFFirst(e.currentTarget.value)} placeholder="filter" /></Table.Th>
                  <Table.Th><TextInput size="xs" value={fLast} onChange={e => setFLast(e.currentTarget.value)} placeholder="filter" /></Table.Th>
                  <Table.Th><TextInput size="xs" value={fCountry} onChange={e => setFCountry(e.currentTarget.value)} placeholder="filter" /></Table.Th>
                  <Table.Th>
                    <Select
                      size="xs"
                      searchable
                      clearable
                      placeholder="Filter"
                      value={fDepLoc}
                      onChange={setFDepLoc}
                      data={departureLocations}
                    />
                  </Table.Th>
                  <Table.Th />
                  <Table.Th>
                    <Select
                      size="xs"
                      searchable
                      clearable
                      placeholder="Filter"
                      value={fArrLoc}
                      onChange={setFArrLoc}
                      data={arrivalLocations}
                    />
                  </Table.Th>
                  <Table.Th />
                  <Table.Th />
                  <Table.Th><TextInput size="xs" value={fReference} onChange={e => setFReference(e.currentTarget.value)} placeholder="filter" /></Table.Th>
                  <Table.Th>
                    <Checkbox
                      size="xs"
                      label={pickupLabel(fPickup)}
                      checked={fPickup === true}
                      indeterminate={fPickup == null}
                      onChange={() => setFPickup(cyclePickup)}
                    />
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={colCount}>
                      <Group justify="center" py="md">
                        <Text c="dimmed" size="sm">No travels match.</Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                )}
                {filtered.map(r => (
                  <Table.Tr key={r.id}>
                    <Table.Td>{r.firstName ?? ''}</Table.Td>
                    <Table.Td>{r.lastName ?? ''}</Table.Td>
                    <Table.Td>{r.countryName ?? ''}</Table.Td>
                    <Table.Td>{r.departureFromName ?? ''}</Table.Td>
                    <Table.Td>{formatDt(r.departureDate)}</Table.Td>
                    <Table.Td>{r.arrivalToName ?? ''}</Table.Td>
                    <Table.Td>{formatDt(r.arrivalDate)}</Table.Td>
                    <Table.Td><Badge variant="light" size="sm">{r.transport ?? '—'}</Badge></Table.Td>
                    <Table.Td>{r.transportReference ?? ''}</Table.Td>
                    <Table.Td>{r.pickupRequired ? 'Yes' : ''}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Stack>
  );
}

function Th({ label, field, sortField, sortDir, onSort }: {
  label: string;
  field: SortField;
  sortField: SortField | null;
  sortDir: SortDir;
  onSort(f: SortField): void;
}) {
  const active = sortField === field;
  const Icon = !active ? IconArrowsSort : sortDir === 'asc' ? IconSortAscending : IconSortDescending;
  return (
    <Table.Th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => onSort(field)}>
      <Group gap={4} wrap="nowrap">
        <Text size="sm" fw={600}>{label}</Text>
        <Icon size={14} opacity={active ? 1 : 0.4} />
      </Group>
    </Table.Th>
  );
}

function cyclePickup(prev: boolean | null): boolean | null {
  // null (both) → true (yes only) → false (no only) → null
  if (prev == null) return true;
  if (prev === true) return false;
  return null;
}

function pickupLabel(v: boolean | null): string {
  if (v == null) return 'Any';
  return v ? 'Yes only' : 'No only';
}

function distinct(xs: (string | null | undefined)[]): string[] {
  const set = new Set<string>();
  for (const x of xs) if (x) set.add(x);
  return Array.from(set).sort();
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDt(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return iso;
  return `${m[1]}-${m[2]}-${m[3]} ${m[4]}:${m[5]}`;
}
